import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { CheckCircle2, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Activate() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [code, setCode] = useState('');
  const [user, setUser] = useState(null);

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (!userData) {
      navigate('/login');
      return;
    }

    const parsedUser = JSON.parse(userData);
    setUser(parsedUser);

    // If already active AND has sessions, redirect to dashboard
    if (parsedUser.is_active && parsedUser.remaining_sessions > 0) {
      navigate('/dashboard');
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/client/activate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ code: code.trim() })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Code d\'activation invalide');
      }

      // Update user data in localStorage
      const updatedUser = { ...user, is_active: true };
      localStorage.setItem('user', JSON.stringify(updatedUser));

      toast.success('Compte activé avec succès ! 🎉');
      setTimeout(() => navigate('/dashboard'), 1500);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black py-20 px-4">
      <div className="max-w-md mx-auto">
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-2xl text-white text-center">
              {user?.remaining_sessions === 0 ? 'Renouveler votre pack' : 'Activer votre compte'}
            </CardTitle>
            <CardDescription className="text-center text-zinc-400">
              {user?.remaining_sessions === 0 
                ? 'Votre pack précédent est terminé. Entrez votre nouveau code pour continuer.'
                : 'Entrez le code d\'activation fourni par votre coach'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {user?.remaining_sessions === 0 && (
              <div className="mb-6 p-4 bg-orange-950 border border-orange-900 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-orange-400 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-orange-300">
                    <p className="font-semibold mb-1">Pack terminé</p>
                    <p className="text-orange-400">
                      Vous avez utilisé toutes vos {user?.total_sessions} séances. 
                      Contactez votre coach pour acheter un nouveau pack et obtenir un code d'activation.
                    </p>
                  </div>
                </div>
              </div>
            )}
            <div className="mb-6 p-4 bg-blue-950 border border-blue-900 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-blue-400 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-blue-300">
                  <p className="font-semibold mb-1">Comment obtenir votre code ?</p>
                  <ol className="list-decimal list-inside space-y-1 text-blue-400">
                    <li>Effectuez votre paiement (virement bancaire)</li>
                    <li>Votre coach générera un code unique</li>
                    <li>Entrez ce code ci-dessous pour activer votre pack</li>
                  </ol>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="code" className="text-zinc-300">Code d'activation</Label>
                <Input
                  id="code"
                  name="code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="SPORT-12S-2026-001"
                  required
                  className="bg-zinc-800 border-zinc-700 text-white font-mono text-center uppercase"
                />
                <p className="text-xs text-zinc-500">Format: SPORT-12S-2026-001</p>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-red-600 hover:bg-red-700"
                disabled={loading}
              >
                {loading ? 'Activation...' : 'Activer mon compte'}
              </Button>
            </form>

            <div className="mt-6 p-4 bg-zinc-800 rounded-lg">
              <h4 className="text-sm font-semibold text-white mb-2 flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-500" />
                Après activation
              </h4>
              <ul className="text-xs text-zinc-400 space-y-1">
                <li>✓ Accès à votre tableau de bord</li>
                <li>✓ Soumission de vos disponibilités hebdomadaires</li>
                <li>✓ Suivi de vos séances</li>
                <li>✓ Historique des réservations</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
